const { darken, lighten } = require('polished');

const PRIMARY = '#0074ff';
const PRIMARY_LIGHT = '#488eff';
const SECONDARY = '#131c19';
const DANGER = '#eb2126';
const SUCCESS = '#42ba96';
const WARNING = '#ffc153';
const INFO = '#38a3ff';
const DISABLED = '#c9ccd4';

const MULTIPLIER_DARK = 0.1;
const MULTIPLIER_DARKER = 0.3;

const MULTIPLIER_LIGHT = 0.1;
const MULTIPLIER_LIGHTER = 0.2;

const greyscale = {
  grey1: '#4A4A4A',
  grey2: '#5F5F5F',
  grey3: '#9B9B9B',
  grey4: '#D8D8D8',
  grey5: '#F3F3F3',
};

const colors = {
  black: '#000',
  white: '#fff',
  text: '#3b3b3b',
  transparent: 'transparent',
  blacks: [
    'rgba(0,0,0,.0125)',
    'rgba(0,0,0,.025)',
    'rgba(0,0,0,.05)',
    'rgba(0,0,0,.1)',
    'rgba(0,0,0,.2)',
    'rgba(0,0,0,.3)',
    'rgba(0,0,0,.4)',
    'rgba(0,0,0,.5)',
    'rgba(0,0,0,.6)',
    'rgba(0,0,0,.7)',
    'rgba(0,0,0,.8)',
    'rgba(0,0,0,.9)',
  ],
  whites: [
    'rgba(255,255,255,.0125)',
    'rgba(255,255,255,.025)',
    'rgba(255,255,255,.05)',
    'rgba(255,255,255,.1)',
    'rgba(255,255,255,.2)',
    'rgba(255,255,255,.3)',
    'rgba(255,255,255,.4)',
    'rgba(255,255,255,.5)',
    'rgba(255,255,255,.6)',
    'rgba(255,255,255,.7)',
    'rgba(255,255,255,.8)',
    'rgba(255,255,255,.9)',
  ],
  // from bootstrap 4
  bsGrays: {
    gray50: '#f9fafb',
    gray100: '#f8f9fa',
    gray200: '#e9ecef',
    gray300: '#dee2e6',
    gray400: '#ced4da',
    gray500: '#adb5bd',
    gray600: '#6c757d',
    gray700: '#495057',
    gray800: '#343a40',
    gray900: '#212529',
  },
  greyscale,
  coolGrey: { grey1: '#034854', grey2: '#ecf1f9', grey3: '#f5f7fa' },
  notiGrey: { grey1: '#717c8a', grey2: '#f0f0f0', grey3: '#f2f2f2' },
  primary: PRIMARY,
  primaryLight: PRIMARY_LIGHT,
  primaryDark: darken(MULTIPLIER_DARK, PRIMARY),
  primaryDarker: darken(MULTIPLIER_DARKER, PRIMARY),
  secondary: SECONDARY,
  secondaryLight: lighten(MULTIPLIER_LIGHT, SECONDARY),
  secondaryLighter: lighten(MULTIPLIER_LIGHTER, SECONDARY),
  secondaryDark: darken(MULTIPLIER_DARK, SECONDARY),
  secondaryDarker: darken(MULTIPLIER_DARKER, SECONDARY),
  disabled: DISABLED,
  danger: DANGER,
  dangerLight: lighten(MULTIPLIER_LIGHT, DANGER),
  dangerLighter: lighten(0.3, DANGER),
  dangerDark: darken(MULTIPLIER_DARK, DANGER),
  success: SUCCESS,
  successLight: lighten(MULTIPLIER_LIGHT, SUCCESS),
  successLighter: lighten(0.3, SUCCESS),
  warning: WARNING,
  info: INFO,
  border: '#979797',
  badge: '#f0654e',

  // Prepare for design-system; more aligned to UI
  // ref: https://theme-ui.com/theme-spec#color
  contentBackground: '#000',

  // For support on app
  textColor: {
    primary: '#343a40',
    secondary: '#6d767e',
    disabled: '#adb5bd',
  },

  backgroundGreen: '#7BD1B6',
  backgroundGreenLight: '#D9F2EA',

  backgroundYellow: '#FFC453',
  backgroundYellowLight: '#FFEECC',

  backgroundNeutral: '#3B3B3B',
  backgroundNeutralLightgrey: '#f7f7f7',

  neutralGreyLight: '#ebebeb',
  neutralGreyLighter: '#fafafa',

  iconColorSecondary: '#898989',

  // Guided Practice
  guidedPractice: {
    thumbnailCardBackground: '#E1F0FE',
    goToGuidedPracticeButtonBackground: 'rgba(59, 59, 59, 0.24)',
    goToGuidedPracticeButtonText: '#FAFAFA',
    statusWarningColor: '#B27500',
  },

  // CLEAR+
  clear: {
    clearDark: '#E00226',
    clearBackground: '#FFEFF2',
    clearGrayDark: '#898989',
    clearLight: '#F2676C',
    clearGrayLight: '#EBEBEB',
  },

  // eggspace
  eggSpace: {
    primary: {
      300: '#70B7FF',
      500: '#3993FF',
      700: '#266BC8',
    },
    gray: {
      100: '#F1F3F5',
      200: '#E9ECEF',
      300: '#DEE2E6',
      500: '#A6ADB5',
      600: '#6D757E',
      900: '#212529',
    },
  },
};

// TODO: organize color preset
const paasColors = {
  contentBackgroundGrey: '#F5F5F5',
  contentDefaultBackgroundColor: '#F9FBFD',

  // sidebar
  sidebarPrimaryBackground: colors.white,
  sidebarPrimaryText: colors.black,
  sidebarSecondaryText: '#9D9D9D',

  activeSubsectionSidebarBackground: '#F0F2F5',
  activeSubsectionSidebarText: '#2C67FE',
  completedSubsectionSidebarText: '#595959',
  completedSubsectionNumberSidebarText: colors.black,

  subsectionTitleBackground: colors.white,
  navBarBackground: colors.white,
  notificationIcon: colors.black,
  notificationDivider: 'hsla(0, 0%, 95%, 1)',

  sidebarPrimaryDivider: '#D9D9D9',
  sidebarSecodaryDivider: '#F0F0F0',
  // default color of sidebar toggle button
  sidebarToggleButton: '#EE6622',

  sidebarPurchaseCourseButton: '#EE6622',

  downloadableItemsBackground: '#FAFAFA',

  contentFooterBackground: colors.white,

  articleDetails: colors.black,

  quizQuestionTitle: colors.black,
  quizQuestionDetails: colors.black,
  quizAnswerDetails: colors.black,
  quizLandingBackground: PRIMARY,

  hint: '#595959',
  badgePaas: '#FB4E4E',
  lightBluePaas: '#E2F1FA',

  videoOverlayHeader: '#C4C4C4',
  videoOverlayDescription: colors.white,
  videoOverlayBackground: colors.black,

  snackbarBackground: '#262626',

  myPath: {
    primary: {
      100: '#E5F1FF',
      300: '#CCE3FF',
      500: '#0074FF',
      700: '#004699',
      900: '#002E66',
    },
    secondary: {
      100: '#FFF9EE',
      300: '#FFEDCB',
      500: '#FFC453',
      700: '#AA7232',
      900: '#744B1B',
    },
    info: {
      100: '#EBF6FF',
      300: '#C3E3FF',
      500: '#38A3FF',
      700: '#22659F',
      900: '#113657',
    },
    success: {
      100: '#ECF8F4',
      300: '#C6EADF',
      500: '#38B554',
      700: '#28705A',
      900: '#14382D',
    },
    error: {
      100: '#FFF1F0',
      300: '#FED1CD',
      500: '#F11903',
      700: '#C91503',
      900: '#650B01',
    },
    warning: {
      100: '#FFF8E5',
      300: '#FFEAB3',
      500: '#FCB103',
      700: '#D48B02',
      900: '#664B00',
    },
    brandColor: {
      skyBlue: '#488EFF',
      lightMustard: '#FFC453',
      lightBlue: '#80E7FF',
      steelGrey: '#717C8A',
    },
    gray: {
      50: '#F9FAFB',
      100: '#F1F3F5',
      200: '#E9ECEF',
      300: '#DEE2E6',
      400: '#CED4DA',
      500: '#A6ADB5',
      600: '#6D757E',
      700: '#495057',
      800: '#343A40',
      900: '#212529',
    },
    streak: {
      yellow: '#FFC453',
      orange: '#FF9800',
      red: '#E36414',
      darkRed: '#A62C2C',
      black: '#000000',
    },
    textColor: {
      primary: '#343A40',
      secondary: '#6D757E',
      disabled: '#A6ADB5',
      white: '#FFFFFF',
    },
    subjects: {
      math1: {
        100: 'hsla(203, 100%, 95%, 1)',
        500: 'hsla(203, 100%, 38%, 1)',
        700: 'hsla(203, 100%, 30%, 1)',
      },
      english: {
        100: 'hsla(220, 59%, 95%, 1)',
        500: 'hsla(220, 59%, 40%, 1)',
        700: 'hsla(220, 59%, 30%, 1)',
      },
      physics: {
        100: 'hsla(357, 69%, 95%, 1)',
        500: 'hsla(357, 70%, 50%, 1)',
        700: 'hsla(357, 70%, 30%, 1)',
      },
      chemistry: {
        100: 'hsla(40, 92%, 95%, 1)',
        500: 'hsla(40, 92%, 64%, 1)',
        700: 'hsla(40, 92%, 30%, 1)',
      },
      biology: {
        100: 'hsla(71, 62%, 95%, 1)',
        500: 'hsla(72, 63%, 52%, 1)',
        700: 'hsla(72, 63%, 30%, 1)',
      },
      tgat: {
        100: 'hsla(21, 100%, 95%, 1)',
        500: 'hsla(19, 100%, 55%, 1)',
        700: 'hsla(19, 100%, 30%, 1)',
      },
      tpat1: {
        100: 'hsla(172, 100%, 95%, 1)',
        500: 'hsla(172, 100%, 32%, 1)',
        700: 'hsla(172, 100%, 20%, 1)',
      },
      tpat3: {
        100: 'hsla(3, 77%, 95%, 1)',
        500: 'hsla(2, 77%, 34%, 1)',
        700: 'hsla(2, 76%, 20%, 1)',
      },
      thai: {
        100: 'hsla(338, 83%, 95%, 1)',
        500: 'hsla(338, 83%, 45%, 1)',
        700: 'hsla(338, 83%, 30%, 1)',
      },
      social: {
        100: 'hsla(303, 58%, 95%, 1)',
        500: 'hsla(303, 58%, 36%, 1)',
        700: 'hsla(303, 57%, 20%, 1)',
      },
    },
  },
};
// TODO: organize color preset
const colorsWithPAAS = {
  ...colors,
  ...paasColors,
};

module.exports = colorsWithPAAS;
